import React from "react";

export interface CardProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  icon?: React.ReactNode;
  className?: string;
  actions?: React.ReactNode;
  variant?: "default" | "highlight" | "bordered" | "gradient";
  padding?: "none" | "sm" | "md" | "lg";
  hover?: boolean;
}

export function Card({
  children,
  title,
  subtitle,
  icon,
  className = "",
  actions,
  variant = "default",
  padding = "lg",
  hover = false,
}: CardProps) {
  const variantStyles = {
    default: "bg-white border border-slate-200/80 shadow-sm shadow-slate-200/70",
    highlight:
      "bg-gradient-to-br from-primary-50 via-white to-secondary-50 border border-primary-100 shadow-sm shadow-primary-100/70",
    bordered: "bg-white border border-primary-200 shadow-sm shadow-primary-100/70",
    gradient:
      "bg-gradient-to-r from-primary-600 to-secondary-600 text-white border-0 shadow-xl",
  };

  const paddingStyles = {
    none: "p-0",
    sm: "p-4",
    md: "p-5",
    lg: "p-6",
  };

  const hoverStyle = hover ? "hover:shadow-xl hover:-translate-y-0.5 cursor-pointer" : "";

  return (
    <div
      className={`
        rounded-2xl transition-all duration-300
        ${variantStyles[variant]}
        ${paddingStyles[padding]}
        ${hoverStyle}
        ${className}
      `
        .trim()
        .replace(/\s+/g, " ")}
    >
      {(title || subtitle || actions || icon) && (
        <div className="mb-5 flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            {icon && (
              <div
                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-xl ${
                  variant === "gradient" ? "text-white" : "text-primary-600"
                }`}
              >
                {icon}
              </div>
            )}
            <div>
              {title && (
                <h2
                  className={`text-lg font-bold ${
                    variant === "gradient" ? "text-white" : "text-slate-950"
                  }`}
                >
                  {title}
                </h2>
              )}
              {subtitle && (
                <p
                  className={`text-sm mt-1 ${
                    variant === "gradient" ? "text-white/90" : "text-slate-500"
                  }`}
                >
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
      )}
      {children}
    </div>
  );
}
